/**
 * gallery-sync — shows only the selected variant's media in the base
 * <media-gallery>.
 *
 * Shopify lays product media out as per-color runs ([front, angle…]) with
 * shared lifestyle shots before, between or after them. Each variant's
 * `featured_media` (emitted by Liquid on the picker — the API returns
 * `featuredMediaId: null`) marks the start of a run. A run is as long as the
 * usual spacing between starts, never reaching the next start; anything no run
 * claims is shared. A variant without featured media uses a same-color
 * sibling's run. Then any shot, run or shared, whose alt text (else filename)
 * names only unselected values of an option is hidden (detail shots
 * uploaded away from their run, or runs over-sized when there are few
 * variants). Colors come from Liquid, so this runs before the schema lands;
 * once it has, every option's values (e.g. Mooring Iron) join in.
 */

/**
 * Parse the picker's `data-variant-media` ("variantId:mediaId:Color|…").
 * @param {string|undefined} attr
 * @returns {Map<string,{mediaId:string,color:string}>} keyed by variant id
 */
export function parseVariantMedia(attr) {
  return new Map(
    (attr ?? '')
      .split('|')
      .filter((entry) => entry.includes(':'))
      .map((entry) => {
        const [variantId, mediaId, ...color] = entry.trim().split(':');
        return [variantId, { mediaId, color: color.join(':') }];
      })
  );
}

// Split on punctuation only, so run-together lifestyle names like
// "f11white_f11allgrey" don't read as a color.
const words = (text) =>
  String(text ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const FILLER_WORDS = new Set(['and', 'for', 'the', 'with']);

const isNameWord = (word) =>
  /^[a-z]{3,}$/.test(word) && !FILLER_WORDS.has(word);

/**
 * Which of `names` a filename/alt text names: the longest full names present
 * ("cobalt-blue" → Cobalt Blue, not Blue; a tie names all of them). A name's
 * words must appear in order, with only codes like "A", "SF" or "NF4" between
 * them ("Metallic-A-white" → Metallic White, but not "Blue … with Navy-Top"
 * → Navy Blue). Failing that, a word only one name uses ("cover-navy" → Navy
 * Blue, "white-sf" → White with Blue Stripe). Numbers, short words and filler
 * like "with" never count on their own.
 * @param {string} text
 * @param {string[]} names
 * @returns {string[]}
 */
export function valuesNamedIn(text, names) {
  const textWords = words(text);
  const present = new Set(textWords);
  const inText = ` ${textWords.join(' ')} `;
  const inTextSkippingCodes = ` ${textWords
    .filter((word) => isNameWord(word) || FILLER_WORDS.has(word))
    .join(' ')} `;
  const isFullyNamed = (name) => {
    const nameWords = words(name);
    const phrase = ` ${nameWords.join(' ')} `;
    return (
      nameWords.length > 0 &&
      (inText.includes(phrase) ||
        (nameWords.every(isNameWord) && inTextSkippingCodes.includes(phrase)))
    );
  };
  const fullMatches = names.filter(isFullyNamed);
  const longest = Math.max(0, ...fullMatches.map((name) => words(name).length));
  const named = fullMatches.filter((name) => words(name).length === longest);
  if (named.length) return named;

  const isUniqueWord = (word) =>
    names.filter((name) => words(name).includes(word)).length === 1;
  return names.filter((name) =>
    words(name).some(
      (word) => isNameWord(word) && isUniqueWord(word) && present.has(word)
    )
  );
}

function distinctAxes(axes) {
  const claimed = new Set();
  return axes
    .filter((axis) => axis.selected != null)
    .map(({ names, selected }) => {
      const own = names.filter(
        (name) => /[a-z]/i.test(name) && !claimed.has(name.toLowerCase())
      );
      names.forEach((name) => claimed.add(name.toLowerCase()));
      return { names: own, selected };
    });
}

function namesOtherSelection({ alt, file }, axes) {
  return axes.some(({ names, selected }) => {
    const fromAlt = valuesNamedIn(alt, names);
    const named = fromAlt.length ? fromAlt : valuesNamedIn(file, names);
    return named.length > 0 && !named.includes(selected);
  });
}

/**
 * Filename + alt text for a gallery item. URLs may sit on the zoom link or
 * any of the lazy-loading image attributes. Shopify fills a blank alt with the
 * product title, which says nothing about the shot ("… Standard Blue
 * Ropehold" would read as Blue), so the title is removed from both. Only a
 * URL's last path segment counts.
 * @param {Element} item
 * @param {string} [productTitle]
 * @returns {{alt:string, file:string}}
 */
function mediaText(item, productTitle) {
  const img = item.querySelector('img');
  const urls = [
    item.querySelector('a[href]')?.getAttribute('href'),
    img?.getAttribute('src'),
    img?.getAttribute('data-src'),
    img?.getAttribute('srcset'),
  ].flatMap((attr) => (attr ?? '').split(/[\s,]+/));
  const fileNames = urls
    .filter((url) => url.includes('/'))
    .map((url) => url.split(/[?#]/)[0].split('/').pop());
  const title = ` ${words(productTitle).join(' ')} `;
  const withoutTitle = (text) => {
    const padded = ` ${words(text).join(' ')} `;
    return title.trim() ? padded.replaceAll(title, ' ') : padded;
  };
  return {
    alt: withoutTitle(img?.getAttribute('alt')),
    file: withoutTitle([...new Set(fileNames)].join(' ')),
  };
}

/**
 * Group gallery media into per-variant runs plus a shared remainder.
 *
 * @param {string[]} mediaIds   gallery media ids in DOM order
 * @param {Iterable<string>} featuredIds  every variant's featured media id
 * @returns {{ groups: Map<string,string[]>, shared: string[] }}
 *          groups: featured media id → its run
 */
export function buildMediaGroups(mediaIds, featuredIds) {
  const featured = new Set(featuredIds);
  const starts = [];
  mediaIds.forEach((id, index) => {
    if (featured.has(id)) starts.push(index);
  });

  const gapCounts = new Map();
  for (let i = 1; i < starts.length; i += 1) {
    const gap = starts[i] - starts[i - 1];
    gapCounts.set(gap, (gapCounts.get(gap) ?? 0) + 1);
  }
  // The most common gap, not the smallest: one color missing its angle shot
  // must not shrink every other color's run.
  let runLength = 1;
  let bestCount = 0;
  gapCounts.forEach((count, gap) => {
    if (count > bestCount || (count === bestCount && gap < runLength)) {
      runLength = gap;
      bestCount = count;
    }
  });

  const groups = new Map();
  const claimed = new Set();
  starts.forEach((start, i) => {
    const end = Math.min(start + runLength, starts[i + 1] ?? mediaIds.length);
    const run = mediaIds.slice(start, end);
    run.forEach((id) => claimed.add(id));
    groups.set(mediaIds[start], run);
  });

  return { groups, shared: mediaIds.filter((id) => !claimed.has(id)) };
}

/**
 * Hide non-visible viewer/thumb items, recompute the slider, and activate
 * `activeId`. Uses the gallery's own methods so slider bookkeeping stays right.
 *
 * @param {Element} galleryEl
 * @param {string[]} visibleIds
 * @param {string|null} activeId
 */
export function applyGalleryVisibility(galleryEl, visibleIds, activeId) {
  const visible = new Set(visibleIds.map(String));

  galleryEl
    .querySelectorAll(
      '.media-viewer__item[data-media-id], .media-thumbs__item[data-media-id]'
    )
    .forEach((item) => {
      item.style.display = visible.has(item.dataset.mediaId) ? '' : 'none';
    });

  if (typeof galleryEl.setVisibleItems === 'function') {
    galleryEl.setVisibleItems();
  }

  if (activeId == null) return;
  const activeItem = galleryEl.querySelector(
    `.media-viewer__item[data-media-id="${CSS.escape(String(activeId))}"]`
  );
  if (activeItem && typeof galleryEl.customSetActiveMedia === 'function') {
    galleryEl.currentItem = null;
    galleryEl.customSetActiveMedia(activeItem, true);
  }
}

/**
 * Move a run's viewer + thumb items to the front of their containers, in
 * order, so the selected variant leads even when its run sits after shared
 * lifestyle media.
 * @param {Element} galleryEl
 * @param {string[]} run
 */
export function moveRunToFront(galleryEl, run) {
  for (const sel of ['.media-viewer__item', '.media-thumbs__item']) {
    [...run].reverse().forEach((mediaId) => {
      const item = galleryEl.querySelector(
        `${sel}[data-media-id="${CSS.escape(mediaId)}"]`
      );
      item?.parentElement?.prepend(item);
    });
  }
}

/**
 * Show the variant's run + shared media, minus anything naming only unselected
 * values of an option. Marks the gallery `data-pf-synced` either way, which
 * releases the CSS that hides its thumbnails until now. A product with no
 * variant media keeps the server-rendered gallery. A variant without featured
 * media uses a same-color sibling's run (NF-4 Metallic White single vs. case);
 * with no such sibling (e.g. EFC-1 Graphite) it shows everything not naming
 * another value, rather than the previous variant's shots.
 *
 * @param {Element} galleryEl
 * @param {Map<string,{mediaId:string,color:string}>} variantMedia  from parseVariantMedia
 * @param {string|number|null|undefined} variantId
 * @param {{names:string[], selected:string|null|undefined}[]} [optionAxes]
 *        every option's value names from the schema, checked after the Liquid colors
 * @param {string} [productTitle]  removed from alt text and file names
 */
export function syncGalleryToVariant(
  galleryEl,
  variantMedia,
  variantId,
  optionAxes,
  productTitle
) {
  if (!galleryEl) return;
  galleryEl.setAttribute('data-pf-synced', '');

  const entries = [...variantMedia.values()];
  if (!entries.some((entry) => entry.mediaId)) return;
  const { mediaId, color } = variantMedia.get(String(variantId)) ?? {};
  const featuredId =
    mediaId ||
    (color &&
      entries.find((entry) => entry.color === color && entry.mediaId)?.mediaId);

  const axes = distinctAxes([
    {
      names: [...new Set(entries.map((entry) => entry.color))],
      selected: color,
    },
    ...(optionAxes ?? []),
  ]);
  const syncKey = JSON.stringify([featuredId, axes]);
  if (syncKey === galleryEl.__pfSyncKey) return;

  let grouping = galleryEl.__pfMediaGroups;
  if (!grouping) {
    const items = [
      ...galleryEl.querySelectorAll('.media-viewer__item[data-media-id]'),
    ];
    grouping = buildMediaGroups(
      items.map((item) => item.dataset.mediaId),
      entries.map((entry) => entry.mediaId).filter(Boolean)
    );
    grouping.textOf = new Map(
      items.map((item) => [item.dataset.mediaId, mediaText(item, productTitle)])
    );
    galleryEl.__pfMediaGroups = grouping;
  }

  const run = grouping.groups.get(featuredId);
  galleryEl.__pfSyncKey = syncKey;

  const candidates = run
    ? [...run, ...grouping.shared]
    : [...grouping.textOf.keys()];
  const visible = candidates.filter(
    (id) =>
      id === featuredId || !namesOtherSelection(grouping.textOf.get(id), axes)
  );
  if (run) moveRunToFront(galleryEl, run);
  applyGalleryVisibility(galleryEl, visible, run ? featuredId : visible[0]);
}
